import { useState, useEffect } from 'react';
import { api, BatchCrawlStatusResponse } from '../api/client';
import { DataRefreshPanel, MarketDataRefreshPanel } from '../components/admin';
import './AdminPage.css';

function CrawlStatusPanel() {
  const [status, setStatus] = useState<BatchCrawlStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      const data = await api.getBatchCrawlStatus();
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="info-card"><p>Loading crawl status...</p></div>;
  if (error) return <div className="info-card"><p className="error">{error}</p></div>;
  if (!status) return null;

  return (
    <div className="info-card">
      <h3>Batch Crawl Status</h3>
      <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
        Last updated: {new Date(status.updatedAt).toLocaleTimeString()}
      </p>

      <div className="crawl-source">
        <div className="crawl-header">
          <span className="crawl-name">Zillow</span>
          <span className="crawl-progress">{status.zillow.progress}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${status.zillow.progress}%` }} />
        </div>
        <div className="crawl-stats">
          <span className="stat success">{status.zillow.success || 0} done</span>
          <span className="stat pending">{status.zillow.pending || 0} pending</span>
          {status.zillow.error ? <span className="stat error">{status.zillow.error} errors</span> : null}
        </div>
      </div>

      <div className="crawl-source">
        <div className="crawl-header">
          <span className="crawl-name">RentCast</span>
          <span className="crawl-progress">{status.rentcast.progress}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${status.rentcast.progress}%` }} />
        </div>
        <div className="crawl-stats">
          <span className="stat success">{status.rentcast.success || 0} done</span>
          <span className="stat pending">{status.rentcast.pending || 0} pending</span>
          {status.rentcast.error ? <span className="stat error">{status.rentcast.error} errors</span> : null}
        </div>
      </div>

      {status.roofstock && (
        <div className="crawl-source">
          <div className="crawl-header">
            <span className="crawl-name">Roofstock Properties</span>
            <span className="crawl-progress">{status.roofstock.progress}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${status.roofstock.progress}%` }} />
          </div>
          <div className="crawl-stats">
            <span className="stat success">{status.roofstock.properties || 0} properties</span>
            <span className="stat success">{(status.roofstock.success || 0) + (status.roofstock.subdivided || 0)} boxes done</span>
            <span className="stat pending">{status.roofstock.pending || 0} pending</span>
            {status.roofstock.error ? <span className="stat error">{status.roofstock.error} errors</span> : null}
          </div>
        </div>
      )}

      <button className="btn btn-secondary" onClick={fetchStatus} style={{ marginTop: '1rem' }}>
        Refresh
      </button>
    </div>
  );
}

export function AdminPage() {
  const [clearing, setClearing] = useState(false);
  const [clearResult, setClearResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleClearAllCrawls = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete ALL crawl data?\n\nThis will remove all cached property data from all markets. This action cannot be undone.'
    );

    if (!confirmed) return;

    setClearing(true);
    setClearResult(null);

    try {
      const result = await api.clearAllCrawls();
      setClearResult({ success: true, message: `${result.message} (${result.deleted} items deleted)` });
    } catch (err) {
      setClearResult({ success: false, message: err instanceof Error ? err.message : 'Failed to clear data' });
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p>Manage data sources and system settings</p>
        </div>
      </div>

      <div className="admin-grid">
        <section className="admin-section full-width">
          <MarketDataRefreshPanel />
        </section>

        <section className="admin-section">
          <CrawlStatusPanel />
        </section>

        <section className="admin-section">
          <DataRefreshPanel />
        </section>

        <section className="admin-section">
          <div className="info-card">
            <h3>Data Management</h3>
            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
              Clear stale or incorrect crawl data from the system.
            </p>
            <button
              className="btn btn-danger"
              onClick={handleClearAllCrawls}
              disabled={clearing}
            >
              {clearing ? 'Clearing...' : 'Clear All Crawl Data'}
            </button>
            {clearResult && (
              <p className={`clear-result ${clearResult.success ? 'success' : 'error'}`}>
                {clearResult.message}
              </p>
            )}
          </div>
        </section>

        <section className="admin-section">
          <div className="info-card">
            <h3>Quick Links</h3>
            <ul className="quick-links">
              <li>
                <a href="https://github.com/aelieve/investment-properties/actions" target="_blank" rel="noopener noreferrer">
                  GitHub Actions
                </a>
              </li>
              <li>
                <a href="https://dash.cloudflare.com" target="_blank" rel="noopener noreferrer">
                  Cloudflare Dashboard
                </a>
              </li>
              <li>
                <a href="/history">
                  Crawl History
                </a>
              </li>
            </ul>
          </div>
        </section>

        <section className="admin-section">
          <div className="info-card">
            <h3>System Info</h3>
            <dl className="system-info">
              <dt>API Endpoint</dt>
              <dd>{localStorage.getItem('propertyApiUrl') || 'https://property-api.aelieve.workers.dev'}</dd>
              <dt>Version</dt>
              <dd>1.0.0</dd>
            </dl>
          </div>
        </section>
      </div>
    </div>
  );
}
